import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

// Variable faces are self-hosted: an operations console must open on a weak
// connection without waiting on a font CDN.
import '@fontsource-variable/space-grotesk';
import '@fontsource-variable/geist';
import '@fontsource-variable/geist-mono';

import './styles/index.css';
import App from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { installViewportVars } from './lib/viewport';
import { ApiError } from './lib/api';

// Publish the phone's real visible height before anything lays itself out.
installViewportVars();

/**
 * Query defaults for a dispatch board.
 *
 * Stock tables are re-read constantly while an operator works a list, so a window
 * blur must not throw the table away and refetch it under their cursor. Thirty
 * seconds of staleness is well inside the sheet's sync cadence, and a single retry
 * covers a dropped packet without hammering the API when it is genuinely down.
 * Authentication and authorisation failures are never retried — retrying a 401 just
 * delays the redirect to the login screen.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && [400, 401, 403, 404, 422].includes(error.status)) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      throwOnError: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root is missing from index.html');
}

createRoot(container).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
